import { OnboardingApps } from '@app/context/constants';
import { useNavigation } from '@app/hooks/useNavigation';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { Button } from '@components/ui';
import { UserQuestionnaireApplicationTopic } from '@dieterApi/user/useUserQuery';
import CheckIcon from '@mui/icons-material/Check';
import { Divider, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';

interface Props {
  questionnaire: UserQuestionnaireApplicationTopic;
}

type Snippet = { status_label: React.ReactElement; intro: React.ReactElement };

function TicketCallToAction({ questionnaire }: Props) {
  const { user, activeCompany } = useUserWithCompany();
  const history = useHistory();
  const {
    setNavigation,
    navigation: { onboarding },
  } = useNavigation();
  const { t } = useTranslation();

  const handleClick = () => {
    setNavigation((nav) => {
      nav.documentPaper = {
        modalOpen: true,
        questionnaire,
        index: 0,
      };
    });
  };

  const appId = onboarding.appId || OnboardingApps.CHECKUP;

  const Snippets: Record<OnboardingApps, Snippet> = {
    [OnboardingApps.CHECKUP]: {
      intro: <></>,
      status_label: <></>,
    },
    [OnboardingApps.WEBSITE_SCAN]: {
      intro: <></>,
      status_label: <></>,
    },
    [OnboardingApps.DSE]: {
      intro: (
        <Trans t={t} i18nKey="dashboard.topic.TicketCallToAction.dse.intro">
          <p>
            Du befindest dich nun in deinem Dashboard. Schalte diesen Bereich jetzt frei, um
            <Link to="#" onClick={handleClick}>
              deine Datenschutzerklärung
            </Link>
            einzubinden und im Anschluss das Impressum für deine Website zu erstellen.
          </p>
        </Trans>
      ),
      status_label: (
        <Trans
          t={t}
          i18nKey="dashboard.topic.TicketCallToAction.dse.status_label"
          components={{ bold: <strong /> }}
          shouldUnescape
        />
      ),
    },
    [OnboardingApps.FACEBOOK]: {
      intro: (
        <Trans t={t} i18nKey="dashboard.topic.TicketCallToAction.dse.intro">
          <p>
            Du befindest dich nun in deinem Dashboard. Schalte diesen Bereich jetzt frei, um
            <Link to="#" onClick={handleClick}>
              deine Datenschutzerklärung
            </Link>
            einzubinden und im Anschluss das Impressum für deine Website zu erstellen.
          </p>
        </Trans>
      ),
      status_label: (
        <Trans
          t={t}
          i18nKey="dashboard.topic.TicketCallToAction.dse.status_label"
          components={{ bold: <strong /> }}
          shouldUnescape
        />
      ),
    },
    [OnboardingApps.INSTAGRAM]: {
      intro: (
        <Trans t={t} i18nKey="dashboard.topic.TicketCallToAction.dse.intro">
          <p>
            Du befindest dich nun in deinem Dashboard. Schalte diesen Bereich jetzt frei, um
            <Link to="#" onClick={handleClick}>
              deine Datenschutzerklärung
            </Link>
            einzubinden und im Anschluss das Impressum für deine Website zu erstellen.
          </p>
        </Trans>
      ),
      status_label: (
        <Trans
          t={t}
          i18nKey="dashboard.topic.TicketCallToAction.dse.status_label"
          components={{ bold: <strong /> }}
          shouldUnescape
        />
      ),
    },
    [OnboardingApps.LINKEDIN]: {
      intro: (
        <Trans t={t} i18nKey="dashboard.topic.TicketCallToAction.dse.intro">
          <p>
            Du befindest dich nun in deinem Dashboard. Schalte diesen Bereich jetzt frei, um
            <Link to="#" onClick={handleClick}>
              deine Datenschutzerklärung
            </Link>
            einzubinden und im Anschluss das Impressum für deine Website zu erstellen.
          </p>
        </Trans>
      ),
      status_label: (
        <Trans
          t={t}
          i18nKey="dashboard.topic.TicketCallToAction.dse.status_label"
          components={{ bold: <strong /> }}
          shouldUnescape
        />
      ),
    },
    [OnboardingApps.TIKTOK]: {
      intro: (
        <Trans t={t} i18nKey="dashboard.topic.TicketCallToAction.dse.intro">
          <p>
            Du befindest dich nun in deinem Dashboard. Schalte diesen Bereich jetzt frei, um
            <Link to="#" onClick={handleClick}>
              deine Datenschutzerklärung
            </Link>
            einzubinden und im Anschluss das Impressum für deine Website zu erstellen.
          </p>
        </Trans>
      ),
      status_label: (
        <Trans
          t={t}
          i18nKey="dashboard.topic.TicketCallToAction.dse.status_label"
          components={{ bold: <strong /> }}
          shouldUnescape
        />
      ),
    },
    [OnboardingApps.XING]: {
      intro: (
        <Trans t={t} i18nKey="dashboard.topic.TicketCallToAction.dse.intro">
          <p>
            Du befindest dich nun in deinem Dashboard. Schalte diesen Bereich jetzt frei, um
            <Link to="#" onClick={handleClick}>
              deine Datenschutzerklärung
            </Link>
            einzubinden und im Anschluss das Impressum für deine Website zu erstellen.
          </p>
        </Trans>
      ),
      status_label: (
        <Trans
          t={t}
          i18nKey="dashboard.topic.TicketCallToAction.dse.status_label"
          components={{ bold: <strong /> }}
          shouldUnescape
        />
      ),
    },
    [OnboardingApps.TOMs]: {
      intro: (
        <Trans t={t} i18nKey="dashboard.topic.TicketCallToAction.toms.intro">
          <p>
            Du befindest dich nun in deinem Dashboard. Schalte diesen Bereich jetzt frei, um{' '}
            <Link to="#" onClick={handleClick}>
              die Dokumentation deiner TOMs
            </Link>{' '}
            freizuschalten und im Anschluss das Berechtigungskonzept für dein Unternehmen zu erstellen.
          </p>
        </Trans>
      ),
      status_label: (
        <Trans
          t={t}
          i18nKey="dashboard.topic.TicketCallToAction.toms.status_label"
          components={{ bold: <strong /> }}
          shouldUnescape
        />
      ),
    },
    [OnboardingApps.IMPRINT]: {
      intro: <></>,
      status_label: <></>,
    },
    [OnboardingApps.VVT]: {
      intro: (
        <Trans t={t} i18nKey="dashboard.topic.TicketCallToAction.vvt.intro">
          <p>
            Du befindest dich nun in deinem Dashboard. Schalte diesen Bereich jetzt frei, um{' '}
            <Link to="#" onClick={handleClick}>
              die Dokumentation deiner TOMs
            </Link>{' '}
            freizuschalten und im Anschluss das Berechtigungskonzept für dein Unternehmen zu erstellen.
          </p>
        </Trans>
      ),
      status_label: (
        <Trans
          t={t}
          i18nKey="dashboard.topic.TicketCallToAction.vvt.status_label"
          components={{ bold: <strong /> }}
          shouldUnescape
        />
      ),
    },
    [OnboardingApps.AVV]: {
      intro: (
        <Trans t={t} i18nKey="dashboard.topic.TicketCallToAction.avv.intro">
          <p>
            Du befindest dich nun in deinem Dashboard. Schalte diesen Bereich jetzt frei, um{' '}
            <Link to="#" onClick={handleClick}>
              deinen Auftragsverarbeitungsvertrag
            </Link>{' '}
            freizuschalten.
          </p>
        </Trans>
      ),
      status_label: (
        <Trans
          t={t}
          i18nKey="dashboard.topic.TicketCallToAction.avv.status_label"
          components={{ bold: <strong /> }}
          shouldUnescape
        />
      ),
    },
    [OnboardingApps.INFO]: {
      intro: (
        <Trans t={t} i18nKey="dashboard.topic.TicketCallToAction.info.intro">
          <p>
            Du befindest dich nun in deinem Dashboard. Schalte diesen Bereich jetzt frei, um{' '}
            <Link to="#" onClick={handleClick}>
              deine Datenschutzhinweise
            </Link>{' '}
            freizuschalten.
          </p>
        </Trans>
      ),
      status_label: (
        <Trans
          t={t}
          i18nKey="dashboard.topic.TicketCallToAction.info.status_label"
          components={{ bold: <strong /> }}
          shouldUnescape
        />
      ),
    },
    [OnboardingApps.DSFA]: {
      intro: (
        <Trans t={t} i18nKey="dashboard.topic.TicketCallToAction.dsfa.intro">
          <p>
            Du befindest dich nun in deinem Dashboard. Schalte diesen Bereich jetzt frei, um{' '}
            <Link to="#" onClick={handleClick}>
              deine Datenschutzfolgeabschätzung
            </Link>{' '}
            freizuschalten.
          </p>
        </Trans>
      ),
      status_label: (
        <Trans
          t={t}
          i18nKey="dashboard.topic.TicketCallToAction.dsfa.status_label"
          components={{ bold: <strong /> }}
          shouldUnescape
        />
      ),
    },
    [OnboardingApps.EMPLOYEE_COMMITMENT]: {
      intro: (
        <Trans t={t} i18nKey="dashboard.topic.TicketCallToAction.employee_commitment.intro">
          <p>
            Du befindest dich nun in deinem Dashboard. Schalte diesen Bereich jetzt frei, um{' '}
            <Link to="#" onClick={handleClick}>
              deine Mitarbeiterverpflichtung
            </Link>{' '}
            freizuschalten.
          </p>
        </Trans>
      ),
      status_label: (
        <Trans
          t={t}
          i18nKey="dashboard.topic.TicketCallToAction.employee_commitment.status_label"
          components={{ bold: <strong /> }}
          shouldUnescape
        />
      ),
    },
  };

  const pricingProduct: Record<OnboardingApps, string> = {
    [OnboardingApps.DSE]: 'dse',
    [OnboardingApps.FACEBOOK]: 'dse',
    [OnboardingApps.INSTAGRAM]: 'dse',
    [OnboardingApps.LINKEDIN]: 'dse',
    [OnboardingApps.TIKTOK]: 'dse',
    [OnboardingApps.XING]: 'dse',
    [OnboardingApps.TOMs]: 'toms',
    [OnboardingApps.CHECKUP]: '',
    [OnboardingApps.WEBSITE_SCAN]: 'dse',
    [OnboardingApps.IMPRINT]: 'dse',
    [OnboardingApps.VVT]: 'vvt',
    [OnboardingApps.AVV]: 'avv',
    [OnboardingApps.INFO]: '',
    [OnboardingApps.DSFA]: '',
    [OnboardingApps.EMPLOYEE_COMMITMENT]: '',
  };

  const handleBuyClick = () => {
    if (activeCompany?.shopifyCallbackUrl) {
      // Redirect to Shopify
      window.location.href = activeCompany.shopifyCallbackUrl;
    } else {
      const product = pricingProduct[appId];
      history.push(
        `/pricing?option=${product ? 'ticket' : appId === OnboardingApps.DSFA ? 'premium' : 'basic'}&product=${product}`
      );
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {Snippets[appId].intro}

      {/* centerpiece with img */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-7 place-items-center bg-body px-12 pt-5">
        <div className="flex flex-col items-center gap-y-6 pb-5">
          <Typography variant="h5" color="text.secondary" align="center">
            {Snippets[appId].status_label}
          </Typography>
          <div className="h-10 w-10 rounded-full grid place-items-center bg-success-500 text-white">
            <CheckIcon />
          </div>
          <Button onClick={handleBuyClick}>{t('dashboard.topic.TicketCallToAction.actions.unlock_document')}</Button>
        </div>
        <img src="/assets/images/DSE_erledigt_01.png" alt="dse erledigt" className="h-[200px] w-auto!" />
      </div>
      <Divider />
    </div>
  );
}

export default TicketCallToAction;
